import Chip from '@mui/material/Chip'
import Grow from '@mui/material/Grow'
import { alpha, useTheme } from '@mui/material/styles'
import React, { useRef } from 'react'

import { ACCOUNT_DEACTIVATION_STATUS, isDeactivationPending, type AccountDeactivationStatus } from 'settings/constants/status'
import T from 'T'

type DeactivationPendingChipProps = {
  /** The account's current status; anything not pending takes the chip away. */
  status?: AccountDeactivationStatus | null
}

const TINT_OPACITY = 0.12

/**
 * Amber rather than the suspension red, so a run in flight doesn't read as an account already gone. Owns
 * its own presence, so a page holds no animation state; and holds the last pending status because the run
 * clears it in the same commit that deactivates the account, which would relabel the chip mid-fade.
 */
const DeactivationPendingChip = ({ status }: DeactivationPendingChipProps) => {
  const theme = useTheme()
  const isPending = isDeactivationPending(status)
  const lastPendingStatus = useRef(status)
  if (isPending) lastPendingStatus.current = status

  const label =
    lastPendingStatus.current === ACCOUNT_DEACTIVATION_STATUS.IN_PROGRESS
      ? T.ACCOUNT_DEACTIVATION_IN_PROGRESS
      : T.ACCOUNT_DEACTIVATION_SCHEDULED

  return (
    <Grow unmountOnExit in={isPending}>
      <Chip
        label={label}
        size="small"
        sx={{
          fontWeight: 600,
          borderColor: theme.palette.warning.main,
          backgroundColor: alpha(theme.palette.warning.main, TINT_OPACITY),
          color: theme.palette.warning.dark,
        }}
        variant="outlined"
      />
    </Grow>
  )
}

export default DeactivationPendingChip
